/** First run: what the page does and what it will ask for (Sprint 103.0, mockup 1). */

import type { ReactNode } from 'react';
import { BackButton, Icon, LanguageSwitch, Notice, useWords } from './common.tsx';

export function Welcome({
  secure,
  onStart,
  onManual
}: {
  readonly secure: boolean;
  readonly onStart: () => void;
  readonly onManual: () => void;
}): ReactNode {
  const { t } = useWords();
  return (
    <main className="screen">
      <div className="row">
        <div className="brand">
          <img src="/icon.png" alt="ArchiSimple" />
          <div>
            <strong>{t('app.name')}</strong>
            <span>{t('app.by')}</span>
          </div>
        </div>
        <div className="spacer" />
        <LanguageSwitch />
      </div>

      <div className="hero" aria-hidden="true">
        <svg viewBox="0 0 350 168" preserveAspectRatio="xMidYMid slice">
          <path
            d="M10 140 Q175 -10 340 140"
            fill="none"
            stroke="var(--color-sun-strong)"
            strokeWidth="2.5"
            strokeDasharray="2 7"
            strokeLinecap="round"
          />
          <path
            d="M0 132 H350"
            stroke="var(--color-border-strong)"
            strokeWidth="1.5"
            strokeDasharray="6 6"
          />
          <circle cx="264" cy="58" r="24" fill="var(--color-sun)" fillOpacity=".25" />
          <circle cx="264" cy="58" r="15" fill="var(--color-sun)" />
          <circle cx="18" cy="134" r="5" fill="var(--color-sun-strong)" />
          <circle cx="332" cy="134" r="5" fill="var(--color-sun-strong)" />
        </svg>
      </div>

      <h1 className="title">{t('welcome.title')}</h1>
      <p className="prose">{t('welcome.intro')}</p>
      {secure ? null : <Notice tone="danger">{t('welcome.insecure')}</Notice>}

      <p className="section">{t('welcome.needs')}</p>
      <div className="card">
        <div className="perm">
          <div className="tile tile--camera">
            <Icon name="camera" size={22} />
          </div>
          <div>
            <strong>{t('welcome.camera')}</strong>
            <span>{t('welcome.cameraWhy')}</span>
          </div>
        </div>
        <div className="perm">
          <div className="tile tile--location">
            <Icon name="pin" size={22} />
          </div>
          <div>
            <strong>{t('welcome.location')}</strong>
            <span>{t('welcome.locationWhy')}</span>
          </div>
        </div>
        <div className="perm">
          <div className="tile tile--motion">
            <Icon name="compass" size={22} />
          </div>
          <div>
            <strong>{t('welcome.motion')}</strong>
            <span>{t('welcome.motionWhy')}</span>
          </div>
        </div>
      </div>

      <div className="spacer" />
      <p className="note row" style={{ alignItems: 'flex-start' }}>
        <Icon name="lock" size={16} />
        <span>{t('welcome.privacy')}</span>
      </p>
      <button type="button" className="btn btn--primary btn--block" onClick={onStart}>
        {t('welcome.start')}
      </button>
      <button type="button" className="btn btn--quiet btn--block" onClick={onManual}>
        {t('welcome.manual')}
      </button>
    </main>
  );
}

/** The iOS step: the permission must be asked from this tap (mockup 2). */
export function MotionStep({
  onAllow,
  onSkip,
  onBack
}: {
  readonly onAllow: () => void;
  readonly onSkip: () => void;
  readonly onBack: () => void;
}): ReactNode {
  const { t } = useWords();
  return (
    <main className="screen">
      <div className="row">
        <BackButton onClick={onBack} />
        <div className="spacer" />
        <LanguageSwitch />
      </div>
      <h1 className="title">{t('motion.title')}</h1>
      <p className="prose">{t('motion.body')}</p>
      <p className="section">{t('motion.preview')}</p>
      <div
        className="card row"
        style={{
          alignItems: 'flex-start',
          padding: '0.875rem 1rem',
          background: 'var(--color-bg-muted)',
          borderStyle: 'dashed'
        }}
      >
        <Icon name="info" />
        <div>
          <strong style={{ display: 'block', fontSize: '0.875rem' }}>{t('motion.prompt')}</strong>
          <span
            className="note"
            style={{ fontSize: '0.8125rem', color: 'var(--color-text-subtle)' }}
          >
            {t('motion.hint')}
          </span>
        </div>
      </div>
      <div className="spacer" />
      <button type="button" className="btn btn--primary btn--block" onClick={onAllow}>
        {t('motion.continue')}
      </button>
      <button type="button" className="btn btn--quiet btn--block" onClick={onSkip}>
        {t('motion.skip')}
      </button>
    </main>
  );
}
